import { useMemo, useState } from "react";
import workspaceService from "api/workspaceService";
import Spinner from "components/Spinner";
import {
  EWorkspaceJobTitleKind,
  IWorkspaceJobTitle,
} from "dto/Workspace/IWorkspaceJobTitle";
import { ENotificationType, notification } from "utils/notification";
import styles from "./styles.module.scss";

// Ustawienia → Zespół → Stopnie/Stanowiska. Słownik kadrowy firmy w BE
// (api/workspace/job-titles, tabela WorkspaceJobTitles) — dawniej wyłącznie localStorage
// (kadry_stopnie_v1 / kadry_stanowiska_v1); BE przy pierwszym odczycie importuje te listy
// z AppSettings albo zasiewa listę startową. Stopień przypisuje się członkowi w metryce
// (Lista osób → „Edytuj" w menu członka); stanowisko członka jest tekstem, a słownik je podpowiada.

// ErrorResponse z BE: errorCodes[0].message (np. „Stopień … ma przypisanych osób").
const errorText = (e: unknown, fallback: string) => {
  const data = (
    e as {
      response?: {
        data?: { errorCodes?: { message?: string }[]; Message?: string };
      };
    }
  )?.response?.data;
  return data?.errorCodes?.[0]?.message ?? data?.Message ?? fallback;
};

const Section = ({
  title,
  kind,
  items,
  readonlyItems = [],
  open,
  onToggle,
  accentColor,
  searchQuery,
  canEdit,
  onSaved,
}: {
  title: string;
  kind: EWorkspaceJobTitleKind;
  items: IWorkspaceJobTitle[];
  readonlyItems?: string[];
  open: boolean;
  onToggle: () => void;
  accentColor: string;
  searchQuery: string;
  canEdit: boolean;
  onSaved: () => void;
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [addValue, setAddValue] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const { mutate: saveJobTitle } = workspaceService.useSaveJobTitle();
  const { mutate: removeJobTitle } = workspaceService.useRemoveJobTitle();

  const q = searchQuery.toLowerCase();

  const activeItems = items.filter(
    (i) => !i.IsArchived && i.Name.toLowerCase().includes(q),
  );
  const archivedItems = items.filter(
    (i) => i.IsArchived && i.Name.toLowerCase().includes(q),
  );
  const filteredReadonly = readonlyItems.filter((s) =>
    s.toLowerCase().includes(q),
  );

  const activeCount =
    items.filter((i) => !i.IsArchived).length + readonlyItems.length;
  const archivedCount = items.filter((i) => i.IsArchived).length;

  const noResults =
    activeItems.length === 0 &&
    archivedItems.length === 0 &&
    filteredReadonly.length === 0 &&
    !!q;

  const fail = (e: unknown, fallback: string) =>
    notification({
      type: ENotificationType.ERROR,
      title: "Stopnie/Stanowiska",
      text: errorText(e, fallback),
    });

  const save = (
    item: Pick<IWorkspaceJobTitle, "Name" | "IsArchived"> & { Id?: string },
    onDone?: () => void,
  ) =>
    saveJobTitle(
      { Id: item.Id, Kind: kind, Name: item.Name, IsArchived: item.IsArchived },
      {
        onSuccess: () => {
          onDone?.();
          onSaved();
        },
        onError: (e) => fail(e, "Nie udało się zapisać pozycji."),
      },
    );

  const handleAdd = () => {
    const v = addValue.trim();
    if (!v) {
      setIsAdding(false);
      return;
    }
    save({ Name: v, IsArchived: false }, () => {
      setAddValue("");
      setIsAdding(false);
    });
  };

  const handleEdit = (item: IWorkspaceJobTitle) => {
    const v = editValue.trim();
    if (!v || v === item.Name) {
      setEditingId(null);
      return;
    }
    save({ ...item, Name: v }, () => setEditingId(null));
  };

  const handleDelete = (item: IWorkspaceJobTitle) =>
    removeJobTitle(item.Id, {
      onSuccess: onSaved,
      onError: (e) => fail(e, "Nie udało się usunąć pozycji."),
    });

  const renderItem = (item: IWorkspaceJobTitle) => {
    const isArchived = item.IsArchived;
    // Stopnia przypisanego komuś nie usuwamy (BE też tego pilnuje) — zostaje archiwizacja.
    const deleteBlocked =
      kind === EWorkspaceJobTitleKind.Grade && item.UsageCount > 0;

    if (editingId === item.Id) {
      return (
        <li key={item.Id} className={styles.itemEdit}>
          <input
            className={styles.itemInput}
            value={editValue}
            autoFocus
            maxLength={200}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleEdit(item);
              if (e.key === "Escape") setEditingId(null);
            }}
          />
          <button className={styles.btnSave} onClick={() => handleEdit(item)}>
            ✓
          </button>
          <button
            className={styles.btnCancel}
            onClick={() => setEditingId(null)}
          >
            ✕
          </button>
        </li>
      );
    }

    return (
      <li
        key={item.Id}
        className={`${styles.item} ${isArchived ? styles.itemArchived : ""}`}
      >
        <span
          className={styles.itemDot}
          style={{ background: accentColor, opacity: isArchived ? 0.3 : 0.75 }}
        />
        <span
          className={`${styles.itemText} ${isArchived ? styles.itemTextArchived : ""}`}
        >
          {item.Name}
        </span>
        {item.UsageCount > 0 && (
          <span
            className={styles.readonlyBadge}
            title="Liczba członków zespołu z tą pozycją"
          >
            {item.UsageCount} os.
          </span>
        )}
        {isArchived && (
          <span className={styles.archivedBadge}>Archiwalne</span>
        )}
        {canEdit && (
          <div className={styles.itemActions}>
            {!isArchived && (
              <button
                className={styles.btnEdit}
                title="Edytuj"
                onClick={() => {
                  setEditingId(item.Id);
                  setEditValue(item.Name);
                }}
              >
                ✎
              </button>
            )}
            <button
              className={isArchived ? styles.btnRestore : styles.btnArchive}
              title={isArchived ? "Przywróć" : "Archiwizuj"}
              onClick={() => save({ ...item, IsArchived: !isArchived })}
            >
              {isArchived ? "↩ Przywróć" : "⊘"}
            </button>
            <button
              className={styles.btnDelete}
              title={
                deleteBlocked
                  ? "Stopień jest przypisany członkom zespołu — zarchiwizuj go"
                  : "Usuń"
              }
              disabled={deleteBlocked}
              style={deleteBlocked ? { opacity: 0.35, cursor: "default" } : undefined}
              onClick={() => !deleteBlocked && handleDelete(item)}
            >
              ✕
            </button>
          </div>
        )}
      </li>
    );
  };

  return (
    <div className={styles.section}>
      <button className={styles.sectionHeader} onClick={onToggle}>
        <span
          className={styles.sectionChevron}
          style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
        >
          ▶
        </span>
        <span className={styles.sectionTitle}>{title}</span>
        <div className={styles.sectionCounts}>
          <span
            className={styles.sectionCount}
            style={{ background: accentColor + "22", color: accentColor }}
          >
            {activeCount}
          </span>
          {archivedCount > 0 && (
            <span className={styles.sectionCountArchived}>
              {archivedCount} arch.
            </span>
          )}
        </div>
      </button>
      {open && (
        <>
          <div className={styles.sectionBody}>
            <ul className={styles.itemList}>
              {activeItems.map(renderItem)}
              {filteredReadonly.map((name) => (
                <li key={`ro_${name}`} className={styles.item}>
                  <span
                    className={styles.itemDot}
                    style={{ background: accentColor, opacity: 0.35 }}
                  />
                  <span className={styles.itemText}>{name}</span>
                  <span
                    className={styles.readonlyBadge}
                    title="Stanowisko wpisane członkowi zespołu, którego nie ma w słowniku"
                  >
                    ze struktury
                  </span>
                </li>
              ))}
              {archivedItems.length > 0 && (
                <>
                  <li className={styles.archivedSeparator}>
                    <span>Archiwalne</span>
                  </li>
                  {archivedItems.map(renderItem)}
                </>
              )}
              {noResults && (
                <li className={styles.emptyMsg}>
                  Brak wyników dla „{searchQuery}"
                </li>
              )}
            </ul>
          </div>
          {canEdit &&
            (isAdding ? (
              <div className={styles.addFormRow}>
                <input
                  className={styles.itemInput}
                  value={addValue}
                  autoFocus
                  maxLength={200}
                  placeholder="Nowa pozycja..."
                  onChange={(e) => setAddValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAdd();
                    if (e.key === "Escape") {
                      setIsAdding(false);
                      setAddValue("");
                    }
                  }}
                />
                <button className={styles.btnSave} onClick={handleAdd}>
                  ✓
                </button>
                <button
                  className={styles.btnCancel}
                  onClick={() => {
                    setIsAdding(false);
                    setAddValue("");
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className={styles.addRow}>
                <button
                  className={styles.btnAdd}
                  onClick={() => setIsAdding(true)}
                >
                  + Dodaj
                </button>
              </div>
            ))}
        </>
      )}
    </div>
  );
};

type SectionKey = "stopnie" | "stanowiska";

const KadroweTab = () => {
  const [open, setOpen] = useState<Record<SectionKey, boolean>>({
    stopnie: true,
    stanowiska: true,
  });
  const [search, setSearch] = useState("");

  const { data, isLoading, refetch } = workspaceService.useGetJobTitles();
  const response = data?.data;

  const grades = useMemo(
    () =>
      (response?.Items ?? []).filter(
        (i) => i.Kind === EWorkspaceJobTitleKind.Grade,
      ),
    [response],
  );
  const positions = useMemo(
    () =>
      (response?.Items ?? []).filter(
        (i) => i.Kind === EWorkspaceJobTitleKind.Position,
      ),
    [response],
  );

  const toggle = (key: SectionKey) =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  if (isLoading && !response) {
    return <Spinner />;
  }

  const canEdit = !!response?.CanEdit;

  return (
    <div className={styles.wrapper}>
      <div className={styles.searchBox}>
        <input
          className={styles.searchInput}
          placeholder="Szukaj..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className={styles.searchClear} onClick={() => setSearch("")}>
            ✕
          </button>
        )}
      </div>
      <Section
        title="Stopnie służbowe"
        kind={EWorkspaceJobTitleKind.Grade}
        items={grades}
        open={open.stopnie}
        onToggle={() => toggle("stopnie")}
        accentColor="#5d49c7"
        searchQuery={search}
        canEdit={canEdit}
        onSaved={() => refetch()}
      />
      <Section
        title="Stanowiska służbowe"
        kind={EWorkspaceJobTitleKind.Position}
        items={positions}
        readonlyItems={response?.PositionsInUse ?? []}
        open={open.stanowiska}
        onToggle={() => toggle("stanowiska")}
        accentColor="#1565c0"
        searchQuery={search}
        canEdit={canEdit}
        onSaved={() => refetch()}
      />
    </div>
  );
};

export default KadroweTab;
